/**
 * MIRA Dialog Component
 * Wraps Shadcn Dialog with MIRA design system styles
 */

import React from 'react';
import {
  Dialog as ShadcnDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import styles from '@/styles/components/layout/dialog.module.css';

export const Dialog = ({
  children,
  show,
  open,
  width,
  className,
  onOpenChange,
  showClose = true,
  ...props
}) => {
  const resolvedOpen = open ?? show;
  const dialogProps = { ...props };

  if (resolvedOpen !== undefined) {
    dialogProps.open = resolvedOpen;
  }

  // Remove legacy props so they don't reach Radix
  if (dialogProps.show !== undefined) {
    delete dialogProps.show;
  }
  if (dialogProps.width !== undefined) {
    delete dialogProps.width;
  }

  const childArray = React.Children.toArray(children);
  const hasDialogContent = childArray.some(
    (child) =>
      React.isValidElement(child) && child.type === DialogContent
  );

  // Check if any descendant already provides DialogTitle / DialogDescription
  // to avoid duplicate accessibility elements
  const hasDialogTitle = childArray.some(
    (child) =>
      React.isValidElement(child) &&
      (child.type === DialogTitle || child.type === DialogTitleMira)
  );
  const hasDialogDescription = childArray.some(
    (child) =>
      React.isValidElement(child) &&
      (child.type === DialogDescription || child.type === DialogDescriptionMira)
  );

  // Accessibility fallbacks: visually-hidden title + suppress describedby warning
  const a11yFallback = (
    <>
      {!hasDialogTitle && (
        <DialogTitle className="sr-only">Dialog</DialogTitle>
      )}
      {!hasDialogDescription && (
        <DialogDescription className="sr-only">Dialog content</DialogDescription>
      )}
    </>
  );

  const mergedContent = hasDialogContent
    ? childArray.map((child) => {
        if (
          React.isValidElement(child) &&
          child.type === DialogContent
        ) {
          return React.cloneElement(child, {
            className: cn(child.props.className, width, className),
            showClose:
              child.props.showClose !== undefined
                ? child.props.showClose
                : showClose,
          }, a11yFallback, child.props.children);
        }
        return child;
      })
    : [
        <DialogContent
          key="mira-dialog-content"
          className={cn(width, className)}
          showClose={showClose}
        >
          {a11yFallback}
          {children}
        </DialogContent>,
      ];

  return (
    <ShadcnDialog {...dialogProps} onOpenChange={onOpenChange}>
      {mergedContent}
    </ShadcnDialog>
  );
};

export const DialogContentMira = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <DialogContent
        ref={ref}
        className={cn(styles.miraDialogContent, className)}
        {...props}
      >
        {children}
      </DialogContent>
    );
  }
);

DialogContentMira.displayName = 'MiraDialogContent';

export const DialogHeaderMira = ({ className, ...props }) => {
  return (
    <DialogHeader className={cn(styles.miraDialogHeader, className)} {...props} />
  );
};

export const DialogTitleMira = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <DialogTitle
        ref={ref}
        className={cn(styles.miraDialogTitle, className)}
        {...props}
      />
    );
  }
);

DialogTitleMira.displayName = 'MiraDialogTitle';

export const DialogDescriptionMira = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <DialogDescription
        ref={ref}
        className={cn(styles.miraDialogDescription, className)}
        {...props}
      />
    );
  }
);

DialogDescriptionMira.displayName = 'MiraDialogDescription';

export const DialogFooterMira = ({ className, ...props }) => {
  return (
    <DialogFooter className={cn(styles.miraDialogFooter, className)} {...props} />
  );
};

// Re-export other Dialog components
export { DialogClose, DialogTrigger } from '@/components/ui/dialog';
